import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchEnrolledVenues, fetchCategoryIcons } from '../services/loyaltyService';

const HeaderContext = createContext();

export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

export const HeaderProvider = ({ children }) => {
  const [headerData, setHeaderData] = useState(() => {
    // Initialize from sessionStorage to persist across page refreshes
    try {
      const cachedCategories = sessionStorage.getItem("bc_header_categories");
      const cachedSelectedCategory = sessionStorage.getItem("bc_header_selected_category");
      const cachedCategoryIconMap = sessionStorage.getItem("bc_header_category_icon_map");
      const cachedUserLocation = sessionStorage.getItem("bc_user_location");

      return {
        categories: cachedCategories ? JSON.parse(cachedCategories) : [],
        selectedCategory: cachedSelectedCategory || 'All',
        onCategoryChange: null,
        categoryIconMap: cachedCategoryIconMap ? JSON.parse(cachedCategoryIconMap) : {},
        userLocation: cachedUserLocation ? JSON.parse(cachedUserLocation) : null,
      };
    } catch {
      return {
        categories: [],
        selectedCategory: 'All',
        onCategoryChange: null,
        categoryIconMap: {},
        userLocation: null,
      };
    }
  });

  // Fetch categories on mount so any page opened directly has them
  useEffect(() => {
    const cachedCategories = sessionStorage.getItem("bc_header_categories");
    const cachedIconMap = sessionStorage.getItem("bc_header_category_icon_map");
    if (cachedCategories && cachedIconMap) return; // already hydrated from cache

    const load = async () => {
      try {
        const [{ companies }, iconMap] = await Promise.all([
          fetchEnrolledVenues(),
          fetchCategoryIcons(),
        ]);
        const categories = [
          "All",
          ...new Set(companies.map((c) => c.category || "Restaurant").filter(Boolean)),
        ];
        setHeaderData((prev) => {
          const merged = {
            ...prev,
            categories: prev.categories.length ? prev.categories : categories,
            categoryIconMap: Object.keys(prev.categoryIconMap).length ? prev.categoryIconMap : iconMap,
          };
          try {
            if (!cachedCategories) sessionStorage.setItem("bc_header_categories", JSON.stringify(merged.categories));
            if (!cachedIconMap) sessionStorage.setItem("bc_header_category_icon_map", JSON.stringify(merged.categoryIconMap));
          } catch { /* ignore */ }
          return merged;
        });
      } catch (e) {
        // Non-critical — header just won't show categories
        console.warn("HeaderContext: failed to preload categories", e);
      }
    };

    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateHeaderData = useCallback((data) => {
    setHeaderData((prev) => {
      const newData = { ...prev, ...data };

      // Persist to sessionStorage for page refresh persistence
      try {
        if (data.categories) {
          sessionStorage.setItem("bc_header_categories", JSON.stringify(data.categories));
        }
        if (data.selectedCategory) {
          sessionStorage.setItem("bc_header_selected_category", data.selectedCategory);
        }
        if (data.categoryIconMap) {
          sessionStorage.setItem("bc_header_category_icon_map", JSON.stringify(data.categoryIconMap));
        }
        // userLocation is already cached separately in LoyaltyDirectory
      } catch {
        // Ignore storage errors
      }

      return newData;
    });
  }, []);

  return (
    <HeaderContext.Provider value={{ headerData, updateHeaderData }}>
      {children}
    </HeaderContext.Provider>
  );
};
